import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, Combobox, Datepicker } from '@ibsys/kit';
import { MissellingTypeOption } from '@/entities/dashboards';

type Props = {
  refusalReasons: string[];
  missellingTypes: MissellingTypeOption[];
  productNames: string[];
  dateFrom: string;
  dateTo: string;
  selectedRefusalReasons: string[];
  selectedMissellingTypes: string[];
  selectedProductNames: string[];
  onChangeDateFrom: (v: string) => void;
  onChangeDateTo: (v: string) => void;
  onChangeRefusalReasons: (v: string[]) => void;
  onChangeMissellingTypes: (v: string[]) => void;
  onChangeProductNames: (v: string[]) => void;
  onReset: () => void;
};

/**
 * Панель фильтров дашборда мисселинга РГС Жизнь.
 * Период (два datepicker'а), мультивыбор причин отказа, типов мисселинга и продуктов,
 * кнопка сброса.
 */
export const MissellingFilters: FC<Props> = ({
  refusalReasons,
  missellingTypes,
  productNames,
  dateFrom,
  dateTo,
  selectedRefusalReasons,
  selectedMissellingTypes,
  selectedProductNames,
  onChangeDateFrom,
  onChangeDateTo,
  onChangeRefusalReasons,
  onChangeMissellingTypes,
  onChangeProductNames,
  onReset,
}) => {
  const { t } = useTranslation('missellingDashboard');

  const refusalReasonOptions = useMemo(
    () => refusalReasons.map((name) => ({ value: name, label: name })),
    [refusalReasons],
  );

  const missellingTypeOptions = useMemo(
    () => missellingTypes.map((typeItem) => ({ value: typeItem.code, label: typeItem.name })),
    [missellingTypes],
  );

  const productNameOptions = useMemo(() => productNames.map((name) => ({ value: name, label: name })), [productNames]);

  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 items-end">
          <Datepicker
            label={t('filters.dateFrom')}
            value={dateFrom}
            outputType="iso-date"
            onChange={(v) => onChangeDateFrom(typeof v === 'string' ? v : '')}
          />
          <Datepicker
            label={t('filters.dateTo')}
            value={dateTo}
            outputType="iso-date"
            onChange={(v) => onChangeDateTo(typeof v === 'string' ? v : '')}
          />
          <Combobox
            isMulti
            label={t('filters.refusalReasons')}
            value={selectedRefusalReasons}
            onChange={(v) => onChangeRefusalReasons((v as string[]) ?? [])}
            options={refusalReasonOptions}
            placeholder={t('filters.refusalReasonsPlaceholder')}
            className="w-full"
          />
          <Combobox
            isMulti
            label={t('filters.missellingTypes')}
            value={selectedMissellingTypes}
            onChange={(v) => onChangeMissellingTypes((v as string[]) ?? [])}
            options={missellingTypeOptions}
            placeholder={t('filters.missellingTypesPlaceholder')}
            className="w-full"
          />
          <Combobox
            isMulti
            label={t('filters.productNames')}
            value={selectedProductNames}
            onChange={(v) => onChangeProductNames((v as string[]) ?? [])}
            options={productNameOptions}
            placeholder={t('filters.productNamesPlaceholder')}
            className="w-full"
          />
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={onReset}>
            {t('filters.reset')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
